/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import AppImage from '../AppImage';
import UserAvatar from './UserAvatar';
import { useBranding } from '../BrandingProvider';
import { useAuth } from '@/contexts/AuthContext';

export const NAVIGATION_ITEMS = [
  {
    key: 'principal',
    section: 'Principal',
    icon: 'LayoutDashboard',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'LayoutDashboard',
        requiredRole: ['user', 'supervisor', 'admin', 'superadmin'],
        tooltip: 'Dashboard personal de asistencia',
      },
      {
        label: 'Mi Portal',
        path: '/mi-portal',
        icon: 'UserCircle2',
        requiredRole: ['user', 'supervisor', 'admin', 'superadmin'],
        tooltip: 'Nómina, vacaciones, permisos y autoservicio personal',
      },
      {
        label: 'Centro de Perfil',
        path: '/profile-center',
        icon: 'User',
        requiredRole: ['user', 'supervisor', 'admin', 'superadmin'],
        tooltip: 'Gestión de perfil personal',
      },
    ],
  },
  {
    key: 'supervision',
    section: 'Supervisión',
    icon: 'Building2',
    items: [
      {
        label: 'Portal de Supervisor',
        path: '/supervisor/portal',
        icon: 'LayoutDashboard',
        requiredRole: ['supervisor', 'admin', 'superadmin'],
        tooltip: 'Portal de gestión de obra y empleados',
      },
      {
        label: 'Gestión de Sitios',
        path: '/supervisor/sites',
        icon: 'Building2',
        requiredRole: ['supervisor', 'admin', 'superadmin'],
        tooltip: 'Administración de sitios de construcción',
      },
    ],
  },
  {
    key: 'people',
    section: 'Personal',
    icon: 'Users',
    items: [
      {
        label: 'Gestión de Empleados',
        path: '/admin/employees',
        icon: 'Users',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Administración de empleados y accesos',
      },
      {
        label: 'Historial de Asistencia',
        path: '/admin/attendance',
        icon: 'BarChart3',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Análisis de asistencia y productividad',
      },
      {
        label: 'Nómina',
        path: '/admin/nomina',
        icon: 'Calculator',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Nómina semanal, aguinaldos y finiquitos',
      },
      {
        label: 'Vacaciones',
        path: '/admin/vacaciones',
        icon: 'Palmtree',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Solicitudes de vacaciones y revisión de RH',
      },
      {
        label: 'Permisos',
        path: '/admin/permisos',
        icon: 'CalendarClock',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Solicitudes de permiso por horas o días',
      },
      {
        label: 'Registro de Incidentes',
        path: '/admin/incidents',
        icon: 'AlertTriangle',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Gestión de incidentes de seguridad',
      },
    ],
  },
  {
    key: 'finance',
    section: 'Finanzas y abastecimiento',
    icon: 'Wallet',
    items: [
      {
        label: 'Finanzas',
        path: '/finanzas',
        icon: 'TrendingUp',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Dashboard financiero por obra',
      },
      {
        label: 'Control Financiero',
        path: '/admin/obras-financiero',
        icon: 'DollarSign',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Gestión financiera de obras y gastos',
      },
      {
        label: 'Compras',
        path: '/admin/compras',
        icon: 'ShoppingCart',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Gestión de compras y proveedores',
      },
      {
        label: 'Inventario',
        path: '/admin/inventario',
        icon: 'Package',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Almacenes, artículos y existencias',
      },
      {
        label: 'Caja General',
        path: '/admin/caja',
        icon: 'Wallet',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Movimientos de caja y cortes',
      },
      {
        label: 'Agenda de Cheques',
        path: '/admin/tesoreria/agenda-cheques',
        icon: 'CalendarCheck',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Cheques pendientes por día y cuenta',
      },
      {
        label: 'Catálogos',
        path: '/admin/catalogos',
        icon: 'FolderOpen',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Gestión de empresas y dependencias',
      },
    ],
  },
  {
    key: 'control',
    section: 'Control y reportes',
    icon: 'FileText',
    items: [
      {
        label: 'Centro de Reportes',
        path: '/admin/reports',
        icon: 'FileText',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Generación y exportación de reportes',
      },
      {
        label: 'Monitoreo de Seguridad',
        path: '/activity-logging-and-security-monitoring-dashboard',
        icon: 'Shield',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Monitoreo de actividad y seguridad',
      },
    ],
  },
  {
    key: 'system',
    section: 'Sistema',
    icon: 'Settings',
    items: [
      {
        label: 'Control de Roles',
        path: '/admin/roles',
        icon: 'Lock',
        requiredRole: ['admin', 'superadmin'],
        tooltip: 'Gestión de roles y permisos',
      },
      {
        label: 'Panel de Sistema',
        path: '/admin/system',
        icon: 'Settings',
        requiredRole: ['superadmin'],
        tooltip: 'Configuración avanzada del sistema',
      },
      {
        label: 'Sincronización',
        path: '/admin/sync',
        icon: 'RefreshCcw',
        requiredRole: ['superadmin'],
        tooltip: 'Control administrativo de sincronización',
      },
    ],
  },
  {
    key: 'support',
    section: 'Ayuda y soporte',
    icon: 'CircleHelp',
    items: [
      {
        label: 'Centro de Ayuda',
        path: '/help',
        icon: 'CircleHelp',
        requiredRole: ['user', 'supervisor', 'admin', 'superadmin'],
        tooltip: 'Guías paso a paso y mapa de procesos de NovaAsistencia',
      },
    ],
  },
];

const RoleBasedSidebar = ({
  isCollapsed = false,
  userRole = 'user',
  onToggleCollapse,
  isMobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { signOut, userProfile, user } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const filteredNavigation = useMemo(() => {
    return NAVIGATION_ITEMS.map(section => ({
      ...section,
      items: section.items.filter(item => item.requiredRole.includes(userRole)),
    })).filter(section => section.items.length > 0);
  }, [userRole]);

  const handleNavigation = useCallback(
    path => {
      navigate(path);
      onMobileClose?.();
    },
    [navigate, onMobileClose]
  );

  const handleLogoClick = useCallback(() => {
    navigate('/dashboard');
    onMobileClose?.();
  }, [navigate, onMobileClose]);

  const handleToggle = useCallback(() => {
    onToggleCollapse?.();
  }, [onToggleCollapse]);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      setLoggingOut(false);
    }
  }, [signOut, navigate]);

  const isActivePath = useCallback(
    path => {
      if (path === '/dashboard') return location.pathname === path;
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    },
    [location.pathname]
  );

  const activeSectionKey = useMemo(
    () =>
      filteredNavigation.find(section =>
        section.items.some(item => isActivePath(item.path))
      )?.key ||
      filteredNavigation[0]?.key ||
      null,
    [filteredNavigation, isActivePath]
  );

  useEffect(() => {
    if (!activeSectionKey) return;
    setExpandedSections(current =>
      current[activeSectionKey]
        ? current
        : { ...current, [activeSectionKey]: true }
    );
  }, [activeSectionKey]);

  const handleSectionToggle = useCallback(
    sectionKey => {
      if (isCollapsed) {
        setExpandedSections(current => ({
          ...current,
          [sectionKey]: true,
        }));
        onToggleCollapse?.();
        return;
      }
      setExpandedSections(current => ({
        ...current,
        [sectionKey]: !current[sectionKey],
      }));
    },
    [isCollapsed, onToggleCollapse]
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        onMobileClose?.();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onMobileClose]);

  // CP-07: navegación translúcida de escritorio, coherente con el resto del workspace.
  const SB = {
    base: 'cp07-sidebar cp08-sidebar',
    header: 'cp07-sidebar-header cp08-sidebar-header',
    border: 'border-white/10',
    sectionText: 'cp08-sidebar-section',
    sectionActive: 'cp08-sidebar-section-active',
    sectionHover: 'cp08-sidebar-hover',
    itemText: 'cp08-sidebar-item',
    itemMuted: 'cp08-sidebar-icon',
    itemHover: 'cp08-sidebar-item-hover',
    activeBg: 'cp07-sidebar-active',
    activeText: 'cp08-sidebar-active-text',
    activeRing: '',
    activeIcon: 'cp08-sidebar-active-icon',
    avatarBg: 'bg-white/10',
    avatarRing: 'ring-white/20',
    overlay: 'bg-slate-950/35',
  };

  const sidebarContent = (
    <>
      <div
        className={`flex h-12 items-center gap-2.5 border-b px-3 ${SB.header} ${SB.border}`}
      >
        <button
          type="button"
          onClick={handleLogoClick}
          className={`flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1.5 py-1 text-left transition-colors ${SB.sectionHover}`}
          title={branding?.nombre_empresa || 'Nova ERP'}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/95 p-0.5 shadow-sm">
            <AppImage
              src={branding?.logo_url || '/assets/images/logo-gyid.png'}
              alt={
                branding?.nombre_empresa
                  ? `${branding.nombre_empresa} Logo`
                  : 'Company Logo'
              }
              className="h-full w-full object-contain"
            />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <span className="cp08-sidebar-product block truncate text-[13px] font-bold leading-tight">
                {branding?.nombre_empresa || 'GY&ID Corporativo'}
              </span>
              <span className="cp08-sidebar-brand mt-0.5 block truncate text-[9px] font-semibold uppercase tracking-[0.14em]">
                Nova Asistencia
              </span>
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={handleToggle}
          className="cp08-sidebar-control hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors md:inline-flex"
          title={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
          aria-label={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
        >
          <Icon
            name={isCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'}
            size={15}
          />
        </button>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 px-1.5 py-1.5">
        {filteredNavigation.map(section => {
          const open = !isCollapsed && expandedSections[section.key] === true;
          const sectionActive = section.items.some(item =>
            isActivePath(item.path)
          );

          return (
            <div key={section.key} className="shrink-0">
              <button
                type="button"
                onClick={() => handleSectionToggle(section.key)}
                title={isCollapsed ? section.section : undefined}
                aria-expanded={open}
                className={`group flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                  sectionActive ? SB.sectionActive : SB.sectionText
                } ${SB.sectionHover} ${isCollapsed ? 'justify-center' : ''}`}
              >
                <Icon
                  name={section.icon}
                  size={15}
                  className={`shrink-0 ${
                    sectionActive
                      ? SB.activeIcon
                      : 'cp08-sidebar-icon group-hover:text-white'
                  }`}
                />
                {!isCollapsed && (
                  <>
                    <span className="min-w-0 flex-1 truncate">
                      {section.section}
                    </span>
                    {sectionActive && !open && (
                      <span className="cp08-sidebar-active-dot h-1.5 w-1.5 shrink-0 rounded-full" />
                    )}
                    <Icon
                      name="ChevronDown"
                      size={14}
                      className={`shrink-0 text-slate-500 transition-transform duration-200 ${
                        open ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </>
                )}
              </button>

              {open && (
                <ul className="mt-0.5 space-y-0.5 pb-1">
                  {section.items.map(item => {
                    const isActive = isActivePath(item.path);
                    return (
                      <li key={item.path}>
                        <button
                          type="button"
                          onClick={() => handleNavigation(item.path)}
                          title={item.tooltip}
                          className={`flex h-[30px] w-full items-center gap-2.5 rounded-md py-1.5 pl-4 pr-2 text-[12px] font-medium transition-colors ${
                            isActive
                              ? `${SB.activeBg} ${SB.activeText} ${SB.activeRing}`
                              : `${SB.itemText} ${SB.itemHover}`
                          }`}
                        >
                          <Icon
                            name={item.icon}
                            size={14}
                            className={`shrink-0 ${isActive ? SB.activeIcon : SB.itemMuted}`}
                          />
                          <span className="min-w-0 flex-1 truncate text-left">
                            {item.label}
                          </span>
                          {isActive && (
                            <span className="cp08-sidebar-active-dot h-1.5 w-1.5 shrink-0 rounded-full" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
        <div className="min-h-0 flex-1" />
      </nav>

      <div className={`border-t p-2.5 ${SB.border} ${SB.header}`}>
        {!isCollapsed && (
          <button
            type="button"
            onClick={() => handleNavigation('/profile-center')}
            className={`mb-1 flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left ${SB.sectionHover}`}
          >
            <UserAvatar
              src={userProfile?.avatar_url || user?.user_metadata?.avatar_url}
              name={userProfile?.full_name || user?.email}
              size="sm"
              className="ring-1 ring-white/25 shadow-sm"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold text-white">
                {userProfile?.full_name || 'Usuario'}
              </p>
              <p className="truncate text-[10px] capitalize text-white/55">
                {userRole}
              </p>
            </div>
          </button>
        )}
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className={`cp08-sidebar-logout flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <Icon
            name={loggingOut ? 'Loader2' : 'LogOut'}
            size={15}
            className={`shrink-0 ${loggingOut ? 'animate-spin' : ''}`}
          />
          {!isCollapsed && (
            <span className="truncate">
              {loggingOut ? 'Cerrando...' : 'Cerrar sesión'}
            </span>
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      {isMobileOpen && (
        <div
          className={`fixed inset-0 z-40 ${SB.overlay} backdrop-blur-sm transition-opacity duration-200 md:hidden`}
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 z-40 flex h-full flex-col border-r
          ${SB.base} ${SB.border}
          transition-all duration-200 ease-out
          ${isCollapsed ? 'w-[56px]' : 'w-[220px]'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default React.memo(RoleBasedSidebar);
